/**
 * Shared canonical types for OCC surfaces.
 *
 * Source of truth. Both occ-dashboard (and any future surface — kiosk,
 * supervisor mobile, etc.) should import from here rather than redefine.
 *
 * Path inside occ-dashboard: re-exported via src/api/types.ts so existing
 * imports keep working.
 */

// ── Geo ────────────────────────────────────────────────────────────────────
export interface Point {
  longitude: number;
  latitude: number;
}

// ── Vehicle telemetry ──────────────────────────────────────────────────────
export interface VehiclePosition {
  time: string;
  /**
   * Absent while a tablet is running but not yet bound to a vehicle — nobody
   * signed in, or no assignment made. Such a fix is keyed by device_id and the
   * UI shows "—" wherever vehicle detail would go.
   */
  vehicle_id?: string;
  /** Present on fixes derived from a device heartbeat. */
  device_id?: string;
  /** Which pipeline produced this fix. */
  source?: "vehicle" | "device";
  /**
   * Bus is heartbeating but has had no working session for hours: powered on,
   * on the network, publishing nothing to the AVL path — so it records no
   * trips and no stop events while looking healthy on the map.
   */
  signed_out?: boolean;
  trip_id?: string;
  route_id?: string;
  location: Point;
  bearing?: number;
  speed?: number;
  current_status?: string;
  occupancy_status?: string;
  occupancy_percentage?: number;
  schedule_relationship?: string;
  is_valid: boolean;
  /**
   * When this bus was last seen moving (RFC3339): the newest of its fixes in
   * the last ten minutes faster than 8 km/h, absent when there was none.
   * Computed server-side so a bus in service keeps reading as moving through a
   * station dwell or a red light instead of flipping to idle at every stop.
   * Older backends omit it, and the resolver then judges by `speed` alone.
   */
  moving_at?: string;
}

/** Derived ops status — what dispatchers actually care about. */
export type VehicleOpsStatus = "ok" | "warn" | "stale" | "crit";

/**
 * Full vehicle lifecycle state, richer than VehicleOpsStatus. Resolved from
 * three independent signals — device heartbeat, GPS fix, and trip/session —
 * because a bus can fail at any one of them independently:
 *
 *   lost_contact    trip is genuinely running, but the device/GPS has gone
 *                   dark mid-trip — the actual emergency case: dispatch
 *                   believes a bus is out there and can no longer verify it.
 *   moving_no_trip  GPS is healthy and shows the bus in motion, but no trip
 *                   is claimed. Dispatch cannot account for the run — nothing
 *                   records its trips or stop events — but the bus itself is
 *                   fine, so this is not an emergency. On this fleet it is
 *                   usually a tablet nobody signed in on. It used to share
 *                   lost_contact, which counted these buses as LATE and drew
 *                   them pulsing red: with most of the fleet signed out, a
 *                   map full of alarms nobody could act on.
 *   offline         no device heartbeat at all — the tablet itself is
 *                   unreachable (powered off, no signal, or never installed).
 *   no_gps          device is heartbeating (app is alive, online) but has no
 *                   valid recent GPS fix — different failure than "offline".
 *   unclaimed       device + GPS both fine, bus not moving, nobody signed in.
 *   standing_by     driver signed in, device + GPS fine, bus not moving, no
 *                   trip claimed.
 *   on_trip         a trip is in progress and the bus is reporting — the
 *                   normal working state; on-time/late is a separate delay
 *                   signal, not part of this enum.
 *
 * unclaimed/standing_by are NOT problems — a bus sitting at the depot with
 * nobody signed in, or a driver waiting between trips, is normal fleet
 * idle time, not something to flag red.
 */
export type VehicleLiveState =
  | "lost_contact"
  | "moving_no_trip"
  | "offline"
  | "no_gps"
  | "unclaimed"
  | "standing_by"
  | "on_trip";

export interface VehicleLiveSignals {
  /** Real-time GPS fix, if the vehicle has ever reported one. */
  position?: VehiclePosition | null;
  /** Device heartbeat timestamp (ms epoch) for whatever tablet is bound to
   *  this vehicle, if any is currently bound. */
  lastHeartbeatAt?: number | null;
  /** True while a driver is genuinely signed in on the bound device — a
   *  current session, not merely a device_sessions row (those are left open
   *  for weeks after a tablet is signed out; see liveFleet.ts). */
  loggedIn?: boolean;
  /** True when a trip_instance for this vehicle is genuinely in_progress. */
  tripInProgress?: boolean;
}

const HEARTBEAT_STALE_SEC = 120;
// 90s, not 60s: in Islamabad traffic a bus can sit between fixes longer than
// a clean-network assumption allows, so 60s was flagging normal congestion
// delay as "no GPS".
const GPS_STALE_SEC = 90;
// Above this, a fix is real driving, not GPS jitter on a parked bus (which
// drifts a couple of km/h at rest).
const MOVING_SPEED_KMH = 8;
/**
 * How long a bus keeps reading as moving after its last fix above
 * MOVING_SPEED_KMH (the fix's `moving_at`). Longer than a station dwell or a
 * signal, shorter than a layover: observed live, ORG-7512 read "moving" at
 * 53 km/h and "Standing by" at 2 km/h thirty seconds later while it sat at a
 * station, and back again as it pulled away — the list reshuffled and the
 * counters changed at every stop of a bus that never stopped working.
 */
export const MOVING_HOLD_SEC = 5 * 60;

/** Seconds since the fix was taken; negative when its clock runs ahead. */
export function fixAgeSec(p: VehiclePosition, now = Date.now()): number {
  return (now - new Date(p.time).getTime()) / 1000;
}

/** A valid fix recent enough to say where the bus is now. */
export function hasFreshFix(p: VehiclePosition | null | undefined, now = Date.now()): boolean {
  if (!p?.is_valid) return false;
  const age = fixAgeSec(p, now);
  return Number.isFinite(age) && age <= GPS_STALE_SEC;
}

/**
 * Whether the tablet is reachable: a recent heartbeat, or a fresh fix.
 *
 * A fresh GPS fix proves the device is online — it cannot publish a position
 * otherwise. The heartbeat comes from /devices, which falls back to
 * device_sessions.last_seen_at; that column only advances on authenticated
 * API activity, so a tablet publishing position every second over MQTT while
 * its session is stale was being reported Offline. Five of thirty-five buses
 * were transmitting live and shown dark on the map.
 */
export function isDeviceOnline(
  lastHeartbeatAt: number | null | undefined,
  position: VehiclePosition | null | undefined,
  now = Date.now(),
): boolean {
  if (hasFreshFix(position, now)) return true;
  return lastHeartbeatAt != null && (now - lastHeartbeatAt) / 1000 <= HEARTBEAT_STALE_SEC;
}

/**
 * Real driving, not GPS jitter on a parked bus: faster than MOVING_SPEED_KMH
 * now, or within MOVING_HOLD_SEC of the last fix that was (`moving_at`, when
 * the backend sends it). A `moving_at` ahead of this browser's clock counts
 * as recent — it is clock skew, and the bus was moving.
 */
export function isMoving(p: VehiclePosition, now = Date.now()): boolean {
  if (p.speed != null && p.speed > MOVING_SPEED_KMH) return true;
  if (!p.moving_at) return false;
  const at = new Date(p.moving_at).getTime();
  return Number.isFinite(at) && now - at <= MOVING_HOLD_SEC * 1000;
}

/** Single source of truth for the full vehicle lifecycle state. */
export function resolveVehicleLiveState(s: VehicleLiveSignals, now = Date.now()): VehicleLiveState {
  const hasGps = hasFreshFix(s.position, now);
  const deviceOnline = isDeviceOnline(s.lastHeartbeatAt, s.position, now);

  // A running trip is proof of a driver on its own (trip_instances.driver_id
  // is set at claim), so it decides between the two trip states whatever the
  // session says.
  if (s.tripInProgress) return deviceOnline && hasGps ? "on_trip" : "lost_contact";
  if (!deviceOnline) return "offline";
  if (!hasGps) return "no_gps";
  // A bus demonstrably in motion with no trip claimed is not "idle" by any
  // honest reading, whatever its login state says. Reported from the field: a
  // bus moving at 51-63 km/h showed "Standing by" because nothing here ever
  // looked at speed.
  if (isMoving(s.position!, now)) return "moving_no_trip";
  return s.loggedIn ? "standing_by" : "unclaimed";
}

export interface VehicleLiveStateMeta {
  label: string;
  /**
   * A literal hex colour, never a CSS variable: L.divIcon markers are built as
   * HTML strings, and the same value has to work there, in the legend and in
   * the list. The map used to keep its own copy of these and painted
   * unclaimed, standing-by and on-trip buses all orange while the legend
   * beside it explained them as orange, blue and green.
   */
  color: string;
  /** Pulses on the map and in the list — kept for the one state that is an
   *  emergency, so a pulse always means one. */
  pulse?: boolean;
}

/** Display metadata for each state — the one place the map markers, the
 *  legend, the list and the counters pull colour/label from, so they can
 *  never drift apart. Hexes on the severity scale match web/shared/tokens.css. */
export const VEHICLE_LIVE_STATE_META: Record<VehicleLiveState, VehicleLiveStateMeta> = {
  lost_contact:   { label: "Lost contact",    color: "#D0342C", pulse: true }, // --sev-crit
  moving_no_trip: { label: "Moving, no trip", color: "#8B5CF6" },
  no_gps:         { label: "No GPS",          color: "#B36F00" },              // --sev-warn
  offline:        { label: "Offline",         color: "#86868B" },              // --text-4
  unclaimed:      { label: "Unclaimed",       color: "#F97316" },
  standing_by:    { label: "Standing by",     color: "#3B82F6" },
  on_trip:        { label: "On trip",         color: "#22C55E" },
};

/** The one marker look that is not a VehicleLiveState: an active panic alarm
 *  outranks whatever the bus is doing. Red like lost contact, but drawn with
 *  an alarm glyph instead of the bus, so the two can be told apart. */
export const PANIC_MARKER_META: VehicleLiveStateMeta = { label: "Panic alarm", color: "#D0342C", pulse: true };

/** Fleet-wide vehicle counts, bucketed by VehicleLiveState. Computed once
 *  (Layout.tsx, via liveFleet.ts) and shared by every consumer that shows an
 *  on-duty number — TopBar and MapStatsOverlay previously each computed their
 *  own from different sources and could disagree.
 *
 *  The buckets partition the roster exactly:
 *  onTime + late + early + lost + noTrip + idle + noGps + offline === total. */
export interface FleetStats {
  /** In service: total − offline − noGps, so every bus reporting a fix, and
   *  a bus whose trip went dark (lost) — its trip says it is out there. */
  onDuty: number;
  total: number;
  /** On a trip, from one minute early to five minutes late. */
  onTime: number;
  /** On a trip, more than five minutes behind schedule. */
  late: number;
  /** On a trip, more than a minute ahead of schedule — off schedule too, but
   *  not late, so it is not counted as LATE. */
  early: number;
  /** A trip in progress whose bus has gone dark (lost_contact). */
  lost: number;
  /** Moving with no trip claimed (moving_no_trip). */
  noTrip: number;
  offline: number;
  noGps: number;
  /** unclaimed + standing_by: reporting, not moving, no trip. */
  idle: number;
}

export interface Vehicle {
  id: string;
  agency_id: string;
  vehicle_id: string;
  vehicle_label?: string;
  license_plate?: string;
  status: string;
}

// ── Alerts / incidents ─────────────────────────────────────────────────────
export type AlertSeverity = "INFO" | "WARNING" | "SEVERE";

/** Unified UI severity. Map AlertSeverity → IncidentSeverity in the UI. */
export type IncidentSeverity = "info" | "warn" | "crit" | "ok";

export interface Me {
  user_id: string;
  employee_id: string;
  first_name?: string;
  last_name?: string;
  display_name: string;
  email?: string;
  phone?: string;
  role: string;
  agency_id: string;
  status?: string;
}

export interface AlertMetadata {
  vehicle_id?: string;
  driver_id?: string;
  trip_id?: string;
  route_id?: string;
  stop_id?: string;
  lat?: number;
  lon?: number;
}

/**
 * The position an alert was raised from, or null when there isn't one.
 *
 * A tablet with no GPS lock reports 0,0 rather than omitting the fix, and the
 * backend stores it verbatim. Rendering that puts a panic alarm off the coast
 * of Africa and sends an operator to Null Island, so it is treated as absent —
 * the same rule avl-processor applies when it refuses to store a 0,0 heartbeat.
 * Both halves must be present: a lone latitude reads as a position and is worse
 * than none.
 */
export function alertFix(m?: AlertMetadata | null): { lat: number; lon: number } | null {
  if (!m || m.lat == null || m.lon == null) return null;
  if (m.lat === 0 && m.lon === 0) return null;
  return { lat: m.lat, lon: m.lon };
}

export interface ServiceAlert {
  id: string;
  agency_id: string;
  alert_type: string;
  cause?: string;
  effect?: string;
  severity: AlertSeverity;
  header_text: string;
  description_text?: string;
  url?: string;
  active_period_start?: string;
  active_period_end?: string;
  is_active: boolean;
  /**
   * Structured payload fields (vehicle, location, trip/route refs).
   * Backend always returns at least {}; clients should prefer this over
   * regex-parsing description_text.
   */
  metadata: AlertMetadata;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * UI-side incident shape, derived from ServiceAlert + local ack state.
 * Includes fields the backend does not yet store (acked_by, acked_at) —
 * these come from local storage until the server-side ack endpoint lands.
 */
export interface Incident {
  id: string;
  severity: IncidentSeverity;
  type: string;            // "PANIC", "STALE_GPS", "SCHEDULE_DEVIATION", …
  title: string;
  description?: string;
  vehicleId?: string;
  /**
   * Human-readable bus identity — the plate an operator would put out over the
   * radio. vehicleId is a UUID and is useless for dispatching help.
   */
  vehiclePlate?: string;
  routeId?: string;
  /** Where the alarm was raised, when the device reported a fix with it. */
  lat?: number;
  lon?: number;
  raisedAt: number;        // epoch ms
  ackedBy?: string;        // local-only until backend lands ack
  ackedAt?: number;
  resolved: boolean;
  resolvedAt?: number;
  /** Hold onto the original alert for action handlers. */
  source: ServiceAlert;
}

/**
 * One drawable route line: the path the bus actually follows on the road,
 * taken from GTFS shapes rather than interpolated between stops.
 */
export interface RouteShape {
  route_id: string;
  shape_id: string;
  route_name: string;
  /** Disambiguates routes sharing a short name (both are "Orange Line" here). */
  route_long_name?: string;
  direction_id: number;
  /** GTFS route_color, no leading '#'. */
  color: string;
  /** [latitude, longitude] pairs, already in Leaflet's order. */
  coordinates: [number, number][];
}

/** A station drawn on the live map — only stops trips actually call at. */
export interface RouteStop {
  stop_id: string;
  code?: string;
  name: string;
  latitude: number;
  longitude: number;
  sequence: number;
}

export interface CreateAlertRequest {
  alert_type: string;
  cause?: string;
  effect?: string;
  severity: AlertSeverity;
  header_text: string;
  description_text?: string;
  active_period_start?: string;
  active_period_end?: string;
}

// ── Messages ───────────────────────────────────────────────────────────────
export type DriverMessageType = "info" | "warning" | "urgent" | "emergency";

export interface DriverMessage {
  id: string;
  agency_id: string;
  recipient_driver_id?: string;
  recipient_vehicle_id?: string;
  message_type: DriverMessageType;
  subject?: string;
  body: string;
  requires_acknowledgment: boolean;
  acknowledged_at?: string;
  expires_at?: string;
  created_by?: string;
  created_at: string;
}

export interface SendMessageRequest {
  recipient_driver_id?: string;
  recipient_vehicle_id?: string;
  message_type: DriverMessageType;
  subject?: string;
  body: string;
  requires_acknowledgment?: boolean;
  expires_at?: string | null;
}

// ── Drivers / scheduling ───────────────────────────────────────────────────
export interface Driver {
  id: string;
  agency_id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  status: string;
  last_login_at?: string;
}

export interface CreateDriverRequest {
  employee_id: string;
  role?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  status?: string;
  pin: string;
  hire_date?: string;
  license_expiry?: string;
  license_number?: string;
  license_class?: string;
  seniority_rank?: number;
}

export interface UpdateDriverRequest {
  employee_id?: string;
  role?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  status?: string;
  hire_date?: string;
  license_expiry?: string;
  license_number?: string;
  license_class?: string;
  seniority_rank?: number | null;
}

export interface ResetDriverPinRequest {
  pin: string;
}

export interface Block {
  id: string;
  agency_id: string;
  block_id: string;
  service_id?: string;
  start_time?: string;
  end_time?: string;
}

export interface VehicleAssignment {
  id: string;
  vehicle_id: string;
  block_id: string;
  service_date: string;
  status: string;
  pull_out_time?: string;
  pull_in_time?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface DriverDuty {
  id: string;
  driver_id: string;
  block_id?: string;
  service_date: string;
  duty_type: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start?: string;
  actual_end?: string;
  status: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateVehicleAssignmentRequest {
  vehicle_id: string;
  block_id: string;
  service_date: string;
  status?: string;
  pull_out_time?: string;
  pull_in_time?: string;
  notes?: string;
}

export interface UpdateVehicleAssignmentRequest {
  vehicle_id?: string;
  block_id?: string;
  service_date?: string;
  status?: string;
  pull_out_time?: string;
  pull_in_time?: string;
  notes?: string;
}

export interface CreateDriverDutyRequest {
  driver_id: string;
  block_id?: string;
  service_date: string;
  duty_type?: string;
  scheduled_start: string;
  scheduled_end: string;
  status?: string;
  notes?: string;
}

export interface UpdateDriverDutyRequest {
  driver_id?: string;
  block_id?: string;
  service_date?: string;
  duty_type?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  status?: string;
  notes?: string;
}

// ── Auth ───────────────────────────────────────────────────────────────────
export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  user_id: string;
  agency_id: string;
  role: string;
}

// ── Misc ───────────────────────────────────────────────────────────────────
export interface ListResponse<T> {
  data: T[];
  total_count: number;
  limit: number;
  offset: number;
}

// ── Mapping helpers ────────────────────────────────────────────────────────
export function severityToUi(s: AlertSeverity): IncidentSeverity {
  switch (s) {
    case "INFO": return "info";
    case "WARNING": return "warn";
    case "SEVERE": return "crit";
  }
}

/**
 * A display name for a driver, or null when the record is not a person.
 *
 * Most of this fleet signs in with per-vehicle accounts — first_name "ORG",
 * last_name "7503", employee_id "ORG7503@ORANGE.METRO" — so rendering the name
 * verbatim puts "Driver: ORG 7503" beside plate ORG-7503, which reads as a
 * person, duplicates the plate, and tells an operator nothing. Those are
 * suppressed rather than invented: the system genuinely does not know who is
 * driving, and saying so is more useful than a plausible-looking name.
 */
export function driverDisplayName(name?: string | null): string | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  // "ORG 7503" — the vehicle-account pattern.
  if (/^ORG\s*\d+$/i.test(trimmed)) return null;
  // Placeholder rows from seeding.
  if (/^(user\s+user|a\s+b)$/i.test(trimmed)) return null;
  return trimmed;
}

/** Derive ops status from a position. Single source of truth. */
export function vehicleStatusFromPosition(p: VehiclePosition, now = Date.now()): VehicleOpsStatus {
  const ts = new Date(p.time).getTime();
  const ageSec = (now - ts) / 1000;
  if (!p.is_valid || ageSec > 60) return "stale";
  if (p.schedule_relationship === "CANCELED") return "warn";
  if ((p.speed ?? 0) === 0 && ageSec > 30) return "warn";
  return "ok";
}

// ── Legacy types (used by pages not yet migrated to typed shapes) ──────────
// These stay as `any` until the schedule/stop-template surfaces are reworked
// against the canonical schemas. Keeping the names exported so existing pages
// compile against the shared module.
export type AutoAssignReport = any;
export type DayPattern = any;
export type ScheduleAuditEvent = any;
export type UpsertStopTemplateRequest = any;

// ── GTFS entities ──────────────────────────────────────────────────────────

export interface Agency {
  id: string;
  agency_id: string;
  name: string;
  url?: string;
  timezone?: string;
  language?: string;
  phone?: string;
  email?: string;
  fare_url?: string;
}

export interface Route {
  id: string;
  agency_id: string;
  route_id: string;
  route_short_name?: string;
  route_long_name: string;
  route_type: number;
  route_color?: string;
  route_text_color?: string;
  route_sort_order?: number;
  is_active?: boolean;
}

export interface Stop {
  id: string;
  agency_id: string;
  stop_id: string;
  stop_code?: string;
  stop_name: string;
  stop_desc?: string;
  location: { latitude: number; longitude: number };
  zone_id?: string;
  location_type?: number;
  parent_station?: string | null;
  wheelchair_boarding?: number;
  is_active?: boolean;
}

export interface Calendar {
  id: string;
  agency_id: string;
  service_id: string;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  start_date: string;
  end_date: string;
}

export interface CalendarDate {
  id: string;
  calendar_id: string;
  exception_date: string;
  exception_type: 1 | 2;
}

export interface ShapeListItem {
  id: string;
  agency_id: string;
  shape_id: string;
  points: number;
}

export interface ShapeFull {
  id: string;
  agency_id: string;
  shape_id: string;
  geometry: {
    type: "LineString";
    coordinates: [number, number][];
  };
}

export interface FeedInfo {
  id?: string;
  agency_id: string;
  feed_publisher_name: string;
  feed_publisher_url: string;
  feed_lang: string;
  default_lang?: string;
  feed_start_date?: string;
  feed_end_date?: string;
  feed_version?: string;
  feed_contact_email?: string;
  feed_contact_url?: string;
}

export interface Trip {
  id: string;
  agency_id: string;
  route_id: string;
  service_id: string;
  trip_id: string;
  trip_headsign?: string;
  trip_short_name?: string;
  direction_id?: number;
  shape_id?: string | null;
  block_id?: string | null;
}

export interface StopTime {
  trip_id?: string;
  stop_id: string;
  stop_sequence: number;
  arrival_time: string;
  departure_time: string;
  stop_headsign?: string;
  pickup_type?: number;
  drop_off_type?: number;
  timepoint?: boolean;
  // Joined from the stop by the server (stopTimeSelect), so a trip's stops can
  // be drawn without fetching the whole stop table.
  stop_name?: string;
  stop_code?: string;
  stop_lat?: number;
  stop_lon?: number;
}

export interface GtfsImportReport {
  agency_id: string;
  routes: number;
  stops: number;
  calendars: number;
  calendar_dates: number;
  shapes: number;
  trips: number;
  stop_times: number;
  feed_info_loaded: boolean;
  warnings?: string[];
}

export type ScheduleImportStatus = "pending" | "validated" | "rejected" | "active" | "archived";

export interface ScheduleImportRow {
  id: number;
  import_id: string;
  row_no: number;
  shift_number?: number | null;
  start_time: string;
  direction: string;
  trip_duration_min?: number | null;
  operational_state: string;
  generated_trip_id?: string | null;
  error: string;
}

export interface ScheduleImportRowInput {
  shift_number?: number | null;
  start_time: string;
  direction: string;
  trip_duration_min?: number | null;
  operational_state: string;
  error?: string;
}

export interface ScheduleDiff {
  a: ScheduleImport;
  b: ScheduleImport;
  summary: { added: number; removed: number; changed: number; a_rows: number; b_rows: number };
  added: ScheduleImportRow[];
  removed: ScheduleImportRow[];
  changed: ScheduleImportRow[];
}

export interface ScheduleImportDetail {
  import: ScheduleImport;
  rows: ScheduleImportRow[];
  row_total: number;
  row_limit: number;
  row_offset: number;
}

export interface RouteStopTemplateStop {
  stop_sequence: number;
  stop_id: string;
  offset_seconds: number;
  dwell_seconds: number;
  [k: string]: any;
}
export interface RouteStopTemplate {
  stops?: RouteStopTemplateStop[];
  direction: string;
  headsign?: string;
  notes?: string;
  [k: string]: any;
}
export interface ScheduleImport {
  filename: string;
  status: string;
  [k: string]: any;
}
export interface ScheduleDryRunResponse {
  dry_run: true;
  [k: string]: any;
}

// Schedule generator: replaces xlsx upload with parameter-driven generation.
export interface HeadwayBand {
  from_min: number;
  to_min: number;       // exclusive
  headway_min: number;
}

export interface GenerateScheduleParams {
  route_id: string;
  day_pattern: string;
  effective_from?: string | null;
  effective_to?: string | null;
  service_start_min: number;   // minutes from midnight, e.g. 360 = 06:00
  service_end_min: number;
  turnaround_min: number;
  bands: HeadwayBand[];
  block_count?: number | null;
  anchor_direction: "upward" | "downward";
  /**
   * How band ticks become trips. Omitted (or "alternating") is the original
   * behaviour: one trip per tick, direction alternating block by block, so a
   * band's headway is the gap between departures anywhere on the route.
   * "both_directions" sends a trip from each terminal on every tick, so the
   * headway is what a rider at a stop gets in each direction.
   */
  dispatch_mode?: DispatchMode;
  notes?: string;
}

export type DispatchMode = "alternating" | "both_directions";

export interface GenerationResult {
  params: GenerateScheduleParams;
  rows: ScheduleImportRow[];
  block_count: number;
  min_blocks: number;
  round_trip_min: number;
  upward_duration_min: number;
  downward_duration_min: number;
  trips_total: number;
  sha256: string;
}

export interface CommitGenerateResponse {
  import: ScheduleImport;
  summary: GenerationResult;
  materialized?: any;
}

// ── Ridership demand (fare-system origin–destination matrix) ────────────────
// Mirrors internal/demand. Every figure is an average per service day over
// the days listed as included in `period`.

/** Demand in one direction of travel during one time bucket. */
export interface DemandDirection {
  /** Riders boarding trips that leave their first stop in this bucket. */
  boardings: number;
  /** link_loads[k] = riders aboard between stop k and stop k+1 (upward numbering). */
  link_loads: number[];
  /** Largest link load and where it occurs: what the service is sized for. */
  peak_load: number;
  peak_link: number;
}

export interface DemandBucket {
  /** Minutes after midnight: when the trip leaves its first stop, or, at the boarding time basis, when riders reach the stop. */
  start_min: number;
  upward: DemandDirection;
  downward: DemandDirection;
  /** od[o][d] = riders from stop o to stop d on trips leaving in this bucket. */
  od: number[][];
}

export interface DemandStop {
  index: number;
  stop_id: string;
  code: string;
  name: string;
  offset_up_sec: number;
  offset_down_sec: number;
  afc_stations: Array<{ afc_station_id: number; name: string }>;
  boardings: number;
  alightings: number;
  /** False when no gate reading was seen: journeys to/from it are inferred. */
  has_entry_taps: boolean;
  has_exit_taps: boolean;
  inbound_scale: number;
}

export interface DemandDay {
  date: string;
  journeys: number;
  included: boolean;
  /** Why the day was left out: far below its weekday's usual level, or not mirrored. */
  reason?: "low" | "no_data";
}

export interface DemandTotals {
  journeys: number;
  /** Both ends read from gates or tickets. */
  observed: number;
  /** No recorded destination; one was inferred. */
  inferred: number;
  unplaced: number;
  same_stop: number;
  unknown_origin: number;
  by_basis: Record<string, number>;
}

export interface DemandAnalysis {
  route: { id: string; short_name: string; long_name: string };
  period: {
    from: string;
    to: string;
    day_pattern: string;
    days: DemandDay[];
    days_included: number;
  };
  /** What a bucket's time means: "trip_departure" or "boarding" (when riders reached the stop). */
  time_basis: "trip_departure" | "boarding";
  bucket_minutes: number;
  /** In upward order. */
  stops: DemandStop[];
  /** Contiguous from the first bucket with demand to the last. */
  buckets: DemandBucket[];
  matrix: number[][];
  matrix_observed: number[][];
  totals: DemandTotals;
  unmapped_stations: Array<{ afc_station_id: number; name: string; journeys_per_day: number }>;
}

export interface DemandStation {
  afc_station_id: number;
  station_no?: string;
  name: string;
  abbreviation?: string;
  line_name?: string;
  stop_id?: string;
  stop_code?: string;
  stop_name?: string;
  stop_source?: "seed" | "auto" | "manual";
}

export interface DemandStatus {
  /** False when the deployment has no fare-system address: nothing will sync. */
  configured: boolean;
  last_attempt_at?: string;
  last_success_at?: string;
  last_error?: string;
  earliest_date?: string;
  latest_date?: string;
  days: number;
  stations: DemandStation[];
  stops: Array<{ stop_id: string; code: string; name: string; route_name: string }>;
}

// ── Trip Instances (runtime trip execution) ────────────────────────────────
export type TripInstanceStatus =
  | "scheduled" | "in_progress" | "completed" | "cancelled" | "no_show"
  /** Started but never reached its final stop; set only by the server's trip closer. */
  | "incomplete";

export interface TripInstance {
  id: string;
  agency_id: string;
  trip_id: string;
  service_date: string;
  driver_id?: string;
  vehicle_id?: string;
  duty_id?: string;
  status: TripInstanceStatus;
  scheduled_start?: string;
  scheduled_end?: string;
  actual_start?: string;
  actual_end?: string;
  current_stop_sequence?: number;
  delay_seconds: number;
  notes?: string;
  // Denormalized human-readable identifiers populated by the backend so
  // dispatchers never have to recognise a trip by UUID prefix.
  route_short_name?: string;
  route_long_name?: string;
  route_color?: string;
  route_text_color?: string;
  headsign?: string;
  driver_name?: string;
  vehicle_label?: string;
  /** Real stop count from the backend (cheap correlated-subquery, populated
   *  on both list and detail queries) — use this for progress display
   *  instead of stop_times.length, which is only hydrated on the detail
   *  (getTripInstance) response, not the list one. */
  total_stops?: number;
  stop_times?: TripInstanceStopTime[];
}

export interface TripInstanceStopTime {
  id: string;
  trip_instance_id: string;
  stop_id: string;
  stop_sequence: number;
  scheduled_arrival?: string;
  scheduled_departure?: string;
  actual_arrival?: string;
  actual_departure?: string;
  arrival_delay_seconds?: number;
  departure_delay_seconds?: number;
  arrival_source?: string;
  departure_source?: string;
  passengers_boarded?: number;
  passengers_alighted?: number;
  skipped: boolean;
  // Denormalized stop identifiers from the join. Always populated; the
  // backend defaults to "" when a stop row is missing.
  stop_name?: string;
  stop_code?: string;
  stop_lat?: number;
  stop_lon?: number;
}

// ── Live-GPS station check-in/check-out (stop_visits) ──────────────────────

/** One live-GPS fix captured at check-in or check-out. */
export interface StopVisitCheckpoint {
  timestampIso: string;
  latitude: number;
  longitude: number;
  distanceMeters: number;
}

/** The driver app's JSON document for one station visit — this is the
 *  `payload` column, already unwrapped (json.RawMessage marshals inline). */
export interface StopVisitPayload {
  tripInstanceId: string;
  tripId: string;
  stopId: string;
  stopSequence: number;
  stopName: string;
  stationLatitude: number;
  stationLongitude: number;
  checkIn?: StopVisitCheckpoint;
  checkOut?: StopVisitCheckpoint;
  dwellSeconds?: number;
}

/** One row from GET /trip-instances/{id}/stop-visits. */
export interface StopVisit {
  id: string;
  agency_id: string;
  trip_instance_id: string;
  trip_id: string;
  stop_id: string;
  stop_sequence: number;
  payload: StopVisitPayload;
  created_at: string;
  updated_at: string;
}

// ── Live progress (schedule + stop_visits + live GPS merged) ───────────────

export type TripLiveStopStatus = "departed" | "at_stop" | "upcoming";
export type TripLiveETABasis = "live_gps" | "trip_avg_speed" | "schedule_plus_delay" | "";

/** One station row from GET /trip-instances/{id}/live-progress. */
export interface TripInstanceLiveProgressStop {
  stop_sequence: number;
  stop_id: string;
  stop_name: string;
  stop_lat: number;
  stop_lon: number;
  scheduled_arrival?: string;
  scheduled_departure?: string;
  status: TripLiveStopStatus;
  check_in_time?: string;
  check_in_distance_m?: number;
  check_out_time?: string;
  check_out_distance_m?: number;
  dwell_seconds?: number;
  arrival_delay_seconds?: number;
  departure_delay_seconds?: number;
  predicted_arrival?: string;
  distance_remaining_m?: number;
  eta_basis?: TripLiveETABasis;
}

/** Response of GET /trip-instances/{id}/live-progress — schedule, the real
 *  check-in/check-out trail, and the live GPS fix merged into one accurate
 *  per-stop timeline, so a dispatcher can verify a claimed trip's bus is
 *  genuinely running. */
export interface TripInstanceLiveProgress {
  trip_instance_id: string;
  status: string;
  vehicle_id?: string;
  is_live: boolean;
  live_location?: Point;
  live_location_age_seconds?: number;
  live_speed_kmh?: number;
  current_stop_sequence: number;
  current_stop_label: string;
  delay_seconds: number;
  stops: TripInstanceLiveProgressStop[];
}

// ── Firmware / device inventory ─────────────────────────────────────────────

export interface FirmwareRelease {
  channel: string;
  version_code: number;
  version_name: string;
  filename: string;
  sha256_hex: string;
  size_bytes: number;
  release_notes: string;
  mandatory: boolean;
  min_supported_version_code: number;
  created_at: string;
}

export interface OtaPolicy {
  channel: string;
  target_version_code: number | null;
  enabled: boolean;
  updated_at: string;
}

export interface DeviceInventoryEntry {
  device_id: string;
  status: string;
  ip_address?: string;
  mac_address?: string;
  serial_number?: string;
  app_version?: string;
  device_model?: string;
  os_version?: string;
  last_heartbeat_at?: number;
}

export interface DeviceInventoryResponse {
  devices: DeviceInventoryEntry[];
}

/** Telemetry device enriched with its active session (driver/vehicle) and the
 *  trip that vehicle is currently running. Unbound devices have bound=false. */
export interface DeviceView extends DeviceInventoryEntry {
  bound: boolean;
  /** Absent when nobody is signed in — including on a signed_out tablet. */
  driver_id?: string;
  driver_name?: string;
  /**
   * The tablet's session has had no authenticated activity for over three
   * hours — the same rule as a fleet position's `signed_out`. Such a device is
   * still bound to its bus (vehicle fields present) but names no driver: the
   * session row outlives the sign-in by days or weeks.
   */
  signed_out?: boolean;
  vehicle_id?: string;
  vehicle_code?: string;
  vehicle_label?: string;
  vehicle_plate?: string;
  session_started_at?: string;
  trip_instance_id?: string;
  trip_status?: string;
  route_short_name?: string;
  trip_headsign?: string;
  current_stop_sequence?: number;
  delay_seconds?: number;
}

export interface DeviceViewResponse {
  devices: DeviceView[];
}

export interface DiagnosticsBroadcastResult {
  device_id: string;
  status: string;
  ok: boolean;
  cmd_id?: string;
  error?: string;
}

export interface DiagnosticsBroadcastResponse {
  action: string;
  total: number;
  published: number;
  skipped: number;
  failed: number;
  results: DiagnosticsBroadcastResult[];
}

// ── PID Devices ────────────────────────────────────────────────────────────

export type PIDDeviceStatus = "pending" | "active" | "disabled" | "reregistration_requested";

export interface PIDDevice {
  id: string;
  agency_id: string;
  hardware_id: string;
  mac_address?: string;
  device_model?: string;
  device_label?: string;
  vehicle_id?: string;
  status: PIDDeviceStatus;
  app_version?: string;
  last_seen_at?: string;
  created_at: string;
  updated_at: string;
  vehicle_code?: string;
  vehicle_label?: string;
}

export interface CreatePIDDeviceRequest {
  hardware_id: string;
  mac_address?: string;
  device_model?: string;
  device_label?: string;
}

export interface UpdatePIDDeviceRequest {
  device_label?: string;
  mac_address?: string;
  device_model?: string;
  vehicle_id?: string;
}

export interface PIDDeviceListResponse {
  data: PIDDevice[];
  total: number;
}

// ── PID Tickers ────────────────────────────────────────────────────────────

export type PIDTickerScope = "global" | "device" | "vehicle";

export interface PIDTicker {
  id: string;
  agency_id: string;
  scope: PIDTickerScope;
  device_id?: string;
  vehicle_id?: string;
  sort_order: number;
  text_en: string;
  text_ur: string;
  icon: string;
  number: string;
  fare: string;
  is_active: boolean;
  start_at?: string;
  end_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePIDTickerRequest {
  scope: PIDTickerScope;
  device_id?: string;
  vehicle_id?: string;
  text_en: string;
  text_ur: string;
  icon?: string;
  number?: string;
  fare?: string;
  is_active?: boolean;
  start_at?: string;
  end_at?: string;
}

export interface UpdatePIDTickerRequest {
  scope?: PIDTickerScope;
  device_id?: string;
  vehicle_id?: string;
  sort_order?: number;
  text_en?: string;
  text_ur?: string;
  icon?: string;
  number?: string;
  fare?: string;
  is_active?: boolean;
  start_at?: string;
  end_at?: string;
}

// ── MAC Change Requests ──────────────────────────────────────────────────

export type MACChangeStatus = "pending" | "approved" | "rejected";

export interface MACChangeRequest {
  id: string;
  agency_id: string;
  device_id: string;
  hardware_id: string;
  old_mac: string;
  new_mac: string;
  status: MACChangeStatus;
  requested_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_note?: string;
}

export interface MACChangeRequestListResponse {
  data: MACChangeRequest[];
  total: number;
}
